import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import { addDays, subDays, format, startOfWeek, isValid, parseISO } from 'date-fns';
import { it as itLocale, enUS } from 'date-fns/locale';
import { Plus, CalendarX2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { useAuth } from '../../hooks/useAuth';
import { scheduleEventsService, ScheduleEventError } from '../../services/schedule-events';
import type { ScheduleOccurrence } from '../../types/schedule-event';
import { ScheduleDayNavigator } from './ScheduleDayNavigator';
import { ScheduleEventCard } from './ScheduleEventCard';
import { ScheduleAttendeesDrawer } from './ScheduleAttendeesDrawer';
import { ScheduleEventFormDialog } from './ScheduleEventFormDialog';
import { ScheduleBulkRegisterDialog } from './ScheduleBulkRegisterDialog';
import { ScheduleAddUserDialog } from './ScheduleAddUserDialog';

interface SchedulePanelProps {
  gymSlug: string;
  canManage: boolean;
  isAdmin: boolean;
  /** YYYY-MM-DD to open on load (from the URL); defaults to today. */
  initialDate?: string;
  /** Called whenever the selected day changes, to sync the URL. */
  onDateChange?: (date: string) => void;
}

const occurrenceKey = (o: Pick<ScheduleOccurrence, 'event_id' | 'occurrence_date'>) =>
  `${o.event_id}:${o.occurrence_date}`;

function parseInitialDate(value?: string): Date {
  if (value) {
    const parsed = parseISO(value);
    if (isValid(parsed)) return parsed;
  }
  return new Date();
}

/**
 * The gym "Palinsesto": a day-by-day list of events/courses with registration.
 * Occurrences are fetched a week at a time so swiping between the days of one
 * week never refetches.
 */
export default function SchedulePanel({ gymSlug, canManage, isAdmin, initialDate, onDateChange }: SchedulePanelProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dateFnsLocale = i18n.language?.startsWith('it') ? itLocale : enUS;

  const [selectedDate, setSelectedDate] = useState(() => parseInitialDate(initialDate));
  const [cache, setCache] = useState<Record<string, ScheduleOccurrence[]>>({});
  const [loading, setLoading] = useState(true);
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [attendeesFor, setAttendeesFor] = useState<ScheduleOccurrence | null>(null);
  const [attendeesOpen, setAttendeesOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduleOccurrence | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<ScheduleOccurrence | null>(null);
  const [bulkFor, setBulkFor] = useState<ScheduleOccurrence | null>(null);
  const [addUserFor, setAddUserFor] = useState<ScheduleOccurrence | null>(null);

  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const weekKey = format(weekStart, 'yyyy-MM-dd');
  const selectedKey = format(selectedDate, 'yyyy-MM-dd');
  const weekLoaded = weekKey in cache;

  // Change the day and mirror it into the URL so the view is deep-linkable.
  const changeDate = useCallback(
    (date: Date) => {
      setSelectedDate(date);
      onDateChange?.(format(date, 'yyyy-MM-dd'));
    },
    [onDateChange]
  );

  useEffect(() => {
    if (weekLoaded) return;
    let cancelled = false;
    setLoading(true);
    const from = weekKey;
    const to = format(addDays(new Date(`${weekKey}T00:00:00`), 6), 'yyyy-MM-dd');
    scheduleEventsService
      .listOccurrences(gymSlug, from, to)
      .then(occurrences => {
        if (!cancelled) setCache(prev => ({ ...prev, [from]: occurrences }));
      })
      .catch(() => {
        if (!cancelled) toast({ title: t('schedule.loadError'), variant: 'destructive' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gymSlug, weekKey, weekLoaded, t, toast]);

  // After create/edit/delete the affected dates are unknown — drop everything
  // and let the effect refetch the visible week.
  const reload = useCallback(() => setCache({}), []);

  // A staff member confirming a registration from a push action tells open tabs.
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'SCHEDULE_REGISTRATION_CONFIRMED') reload();
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, [reload]);

  const updateOccurrence = useCallback(
    (target: ScheduleOccurrence, patch: Partial<ScheduleOccurrence>) => {
      setCache(prev => {
        const next: Record<string, ScheduleOccurrence[]> = {};
        for (const [week, occurrences] of Object.entries(prev)) {
          next[week] = occurrences.map(o =>
            occurrenceKey(o) === occurrenceKey(target) ? { ...o, ...patch } : o
          );
        }
        return next;
      });
    },
    []
  );

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => changeDate(addDays(selectedDate, 1)),
    onSwipedRight: () => changeDate(subDays(selectedDate, 1)),
    delta: 50,
    trackMouse: false,
  });

  const handleRegister = async (occurrence: ScheduleOccurrence) => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    setPendingKey(occurrenceKey(occurrence));
    try {
      const meta = await scheduleEventsService.register(gymSlug, occurrence.event_id, occurrence.occurrence_date);
      updateOccurrence(occurrence, {
        registered: true,
        registration_status: meta.status,
        registrations_count: meta.registrations_count,
        pending_count: occurrence.pending_count + (meta.status === 'pending' ? 1 : 0),
        full: meta.full,
      });
    } catch (err) {
      if (err instanceof ScheduleEventError && err.code === 'schedule_event_full') {
        updateOccurrence(occurrence, { full: true });
        toast({ title: t('schedule.fullError'), variant: 'destructive' });
      } else if (err instanceof ScheduleEventError && err.code === 'occurrence_cancelled') {
        updateOccurrence(occurrence, { cancelled: true });
        toast({ title: t('schedule.cancelledError'), variant: 'destructive' });
      } else {
        toast({
          title: err instanceof Error ? err.message : t('errors.unexpectedError'),
          variant: 'destructive',
        });
      }
    } finally {
      setPendingKey(null);
    }
  };

  const handleUnregister = async (occurrence: ScheduleOccurrence) => {
    setPendingKey(occurrenceKey(occurrence));
    const wasPending = occurrence.registration_status === 'pending';
    try {
      await scheduleEventsService.unregister(gymSlug, occurrence.event_id, occurrence.occurrence_date);
      updateOccurrence(occurrence, {
        registered: false,
        registration_status: null,
        registrations_count: Math.max(0, occurrence.registrations_count - 1),
        pending_count: Math.max(0, occurrence.pending_count - (wasPending ? 1 : 0)),
        full: false,
      });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setPendingKey(null);
    }
  };

  const handleShowAttendees = (occurrence: ScheduleOccurrence) => {
    if (!user) {
      toast({ title: t('schedule.loginToSeeAttendees'), variant: 'destructive' });
      return;
    }
    setAttendeesFor(occurrence);
    setAttendeesOpen(true);
  };

  const handleToggleCancelled = (occurrence: ScheduleOccurrence) => {
    if (occurrence.cancelled) {
      scheduleEventsService
        .restoreOccurrence(gymSlug, occurrence.event_id, occurrence.occurrence_date)
        .then(() => updateOccurrence(occurrence, { cancelled: false }))
        .catch(err =>
          toast({
            title: err instanceof Error ? err.message : t('errors.unexpectedError'),
            variant: 'destructive',
          })
        );
    } else {
      setCancelConfirm(occurrence);
    }
  };

  const handleConfirmCancel = async () => {
    const occurrence = cancelConfirm;
    if (!occurrence) return;
    try {
      await scheduleEventsService.cancelOccurrence(gymSlug, occurrence.event_id, occurrence.occurrence_date);
      updateOccurrence(occurrence, { cancelled: true });
      toast({ title: t('schedule.cancelSuccess') });
    } catch (err) {
      toast({
        title: err instanceof Error ? err.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setCancelConfirm(null);
    }
  };

  const dayOccurrences = (cache[weekKey] ?? []).filter(o => o.occurrence_date === selectedKey);
  const showLoading = loading && !weekLoaded;

  return (
    <div {...swipeHandlers}>
      <ScheduleDayNavigator date={selectedDate} onDateChange={changeDate} />

      <div className="space-y-2 pt-3 pb-24">
        {showLoading ? (
          <>
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </>
        ) : dayOccurrences.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center space-y-2">
              <CalendarX2 className="h-8 w-8 mx-auto text-muted-foreground/50" />
              <p className="text-muted-foreground text-sm">{t('schedule.emptyDay')}</p>
            </CardContent>
          </Card>
        ) : (
          dayOccurrences.map(occurrence => (
            <ScheduleEventCard
              key={occurrenceKey(occurrence)}
              occurrence={occurrence}
              canManage={canManage}
              isAdmin={isAdmin}
              busy={pendingKey === occurrenceKey(occurrence)}
              isLoggedIn={!!user}
              onRegister={handleRegister}
              onUnregister={handleUnregister}
              onShowAttendees={handleShowAttendees}
              onEdit={o => {
                setEditing(o);
                setFormOpen(true);
              }}
              onToggleCancelled={handleToggleCancelled}
              onAddUser={o => setAddUserFor(o)}
              onBulkRegister={o => setBulkFor(o)}
            />
          ))
        )}
      </div>

      {canManage && (
        <Button
          aria-label={t('schedule.addEvent')}
          className="fixed z-50 rounded-full h-14 w-14 p-0 shadow-lg right-4 md:right-6 bottom-[calc(4.5rem+env(safe-area-inset-bottom))] md:bottom-6"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <Plus className="h-6 w-6" />
        </Button>
      )}

      <ScheduleAttendeesDrawer
        open={attendeesOpen}
        onOpenChange={setAttendeesOpen}
        gymSlug={gymSlug}
        eventId={attendeesFor?.event_id ?? null}
        occurrenceDate={attendeesFor?.occurrence_date ?? null}
        onChanged={reload}
      />

      {canManage && (
        <ScheduleEventFormDialog
          open={formOpen}
          onOpenChange={setFormOpen}
          gymSlug={gymSlug}
          editing={editing}
          onSaved={reload}
          onDeleted={reload}
        />
      )}

      {canManage && (
        <ScheduleAddUserDialog
          open={!!addUserFor}
          onOpenChange={open => !open && setAddUserFor(null)}
          gymSlug={gymSlug}
          eventId={addUserFor?.event_id ?? null}
          occurrenceDate={addUserFor?.occurrence_date ?? null}
          eventTitle={addUserFor?.title ?? ''}
          dayLabel={addUserFor ? format(parseISO(addUserFor.starts_at), 'EEEE d MMM, HH:mm', { locale: dateFnsLocale }) : ''}
          onDone={reload}
        />
      )}

      {isAdmin && (
        <ScheduleBulkRegisterDialog
          open={!!bulkFor}
          onOpenChange={open => !open && setBulkFor(null)}
          gymSlug={gymSlug}
          eventId={bulkFor?.event_id ?? null}
          eventTitle={bulkFor?.title ?? ''}
          untilDefault={bulkFor?.recurrence_ends_on ?? null}
          onDone={reload}
        />
      )}

      {/* Cancelling one occurrence ("not happening this time") needs a confirm:
          registrants will see it as cancelled. */}
      <AlertDialog open={!!cancelConfirm} onOpenChange={open => !open && setCancelConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('schedule.cancelConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('schedule.cancelConfirmBody', {
                title: cancelConfirm?.title ?? '',
                count: cancelConfirm?.registrations_count ?? 0,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleConfirmCancel}
            >
              {t('schedule.cancelOccurrence')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
